import React from 'react';
import { PageConfig, PageSection } from 'shared-types';
import HeroBannerEditor from './editors/HeroBannerEditor';
import FeatureCardsEditor from './editors/FeatureCardsEditor';
import ArticleListEditor from './editors/ArticleListEditor';
import CallToActionEditor from './editors/CallToActionEditor';
import ProductPlansSectionEditor from './editors/ProductPlansSectionEditor';

interface PageEditorProps {
    page: PageConfig;
    onChange: (newPage: PageConfig) => void;
    onBack: () => void;
}

const SectionComponentEditor: React.FC<{ section: PageSection, onChange: (newSection: PageSection) => void }> = ({ section, onChange }) => {
    switch (section.type) {
        case 'HeroBanner':
            return <HeroBannerEditor section={section} onChange={onChange} />;
        case 'FeatureCards':
            return <FeatureCardsEditor section={section} onChange={onChange} />;
        case 'ArticleList':
            return <ArticleListEditor section={section} onChange={onChange} />;
        case 'CallToAction':
            return <CallToActionEditor section={section} onChange={onChange} />;
        case 'ProductPlans':
            return <ProductPlansSectionEditor section={section} onChange={onChange} />;
        default:
            return <div className="p-4 bg-red-100 text-red-700 rounded-md">Unknown section type.</div>;
    }
};

const PageEditor: React.FC<PageEditorProps> = ({ page, onChange, onBack }) => {

    const handleSectionChange = (index: number, newSection: PageSection) => {
        const newSections = [...page.sections];
        newSections[index] = newSection;
        onChange({ ...page, sections: newSections });
    };

    const handleRemoveSection = (index: number) => {
        const newSections = page.sections.filter((_, i) => i !== index);
        onChange({ ...page, sections: newSections.map((s, i) => ({ ...s, order: i + 1 })) });
    };

    const handleMoveSection = (index: number, direction: 'up' | 'down') => {
        const newSections = [...page.sections];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newSections.length) return;

        [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
        onChange({ ...page, sections: newSections.map((s, i) => ({ ...s, order: i + 1 })) });
    };

    return (
        <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
                <button onClick={onBack} className="text-cyan-600 dark:text-cyan-400 font-semibold hover:underline">&larr; Back to Sites</button>
                <div className="w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
                <h2 className="text-xl font-bold">Editing: {page.id.charAt(0).toUpperCase() + page.id.slice(1)} Page</h2>
            </div>

            <div className="space-y-6">
                {page.sections.sort((a, b) => a.order - b.order).map((section, index) => (
                    <div key={section.id} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="p-3 flex justify-between items-center bg-slate-100 dark:bg-slate-700/50 rounded-t-lg border-b border-slate-200 dark:border-slate-700">
                           <div className="flex items-center gap-4">
                             <span className="font-mono text-xs text-slate-500 dark:text-slate-400">#{section.order}</span>
                             <h4 className="font-bold text-slate-800 dark:text-slate-200">{section.type}</h4>
                           </div>
                           <div className="flex items-center gap-2">
                                <button onClick={() => handleMoveSection(index, 'up')} disabled={index === 0} className="p-1 disabled:opacity-30 disabled:cursor-not-allowed">🔼</button>
                                <button onClick={() => handleMoveSection(index, 'down')} disabled={index === page.sections.length - 1} className="p-1 disabled:opacity-30 disabled:cursor-not-allowed">🔽</button>
                                <button onClick={() => handleRemoveSection(index)} className="px-3 py-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-semibold rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 transition">Delete</button>
                           </div>
                        </div>
                        <div className="p-4">
                            <SectionComponentEditor section={section} onChange={(newSection) => handleSectionChange(index, newSection)} />
                        </div>
                    </div>
                ))}
            </div>
            {/* Add New Section UI could be added here */}
        </div>
    );
};

export default PageEditor;